/**
 * Square hosted checkout.
 *
 * Pay-on-delivery stays the default and the primary path — this is an
 * additional option for customers who would rather settle up by card before
 * the truck arrives.
 *
 * There are two ways to reach Square, and the difference matters:
 *
 *  1. EXACT AMOUNT (preferred). The Cloudflare Worker holds a Square access
 *     token as a secret and calls Square's Payment Links API to mint a
 *     one-time link for this order's precise total. The customer sees the
 *     amount already filled in and cannot mistype it.
 *
 *  2. CUSTOM AMOUNT (fallback). A single reusable "customer enters the amount"
 *     checkout link created in the Square dashboard. No server, no token —
 *     but the customer has to key in the total themselves, so we show it to
 *     them alongside the button.
 *
 * If neither is configured, `isSquareEnabled()` is false and no Square UI
 * renders anywhere. That is deliberate: a payment button that goes nowhere is
 * worse than no payment button.
 */

/**
 * Reusable dashboard checkout link, used when the Worker endpoint is
 * unavailable. Create it in Square: Online Checkout → Create payment link →
 * "Accept a payment" → enable "Let the customer enter the amount".
 *
 * Leave empty to disable the fallback.
 */
export const SQUARE_CUSTOM_AMOUNT_LINK = 'https://square.link/u/pvn8O0Iv'

/**
 * Worker route that mints an exact-amount link. Implemented in
 * public/cloudflare-worker.js; requires the SQUARE_ACCESS_TOKEN and
 * SQUARE_LOCATION_ID secrets to be set on the Worker.
 */
const SQUARE_LINK_ENDPOINT = '/api/square-checkout'

/**
 * Whether the Worker route has been deployed. Off for now — we're using the
 * dashboard payment link above, which needs no access token. Flip this to true
 * once SQUARE_ACCESS_TOKEN is set on the Worker and the /api/square-checkout
 * route exists; the exact-amount link will then take over automatically and
 * the dashboard link stays as the fallback.
 */
export const SQUARE_EXACT_AMOUNT_ENABLED = false

export function isSquareEnabled(): boolean {
  return SQUARE_EXACT_AMOUNT_ENABLED || SQUARE_CUSTOM_AMOUNT_LINK.length > 0
}

export interface SquareCheckoutRequest {
  orderNumber: string
  /** Order total in dollars. */
  amount: number
  name: string
  email: string
  phone: string
}

export interface SquareCheckoutResult {
  url: string
  /**
   * True when the link already carries the order total. False means the
   * customer must type the amount on Square's page, so the UI has to show it.
   */
  exactAmount: boolean
}

/**
 * Resolve a Square checkout URL for an order. Never throws — returns null when
 * no route is available so the caller can fall back to pay-on-delivery.
 */
export async function createSquareCheckoutUrl(
  req: SquareCheckoutRequest,
): Promise<SquareCheckoutResult | null> {
  if (SQUARE_EXACT_AMOUNT_ENABLED) {
    try {
      const res = await fetch(SQUARE_LINK_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(req),
      })
      if (res.ok) {
        const data = (await res.json()) as { url?: string }
        if (data.url) return { url: data.url, exactAmount: true }
      } else {
        console.error(`Square link failed (${res.status}): ${await res.text().catch(() => '')}`)
      }
    } catch (err) {
      console.error('Square link error:', err)
    }
  }

  if (SQUARE_CUSTOM_AMOUNT_LINK) {
    return { url: SQUARE_CUSTOM_AMOUNT_LINK, exactAmount: false }
  }

  return null
}
